import { AsperaService } from '@adgate/common/services/aspera-service';
import { BaseService } from '@adgate/common/services/base-service';
import { DialogService } from '@adgate/common/services/dialog-service';
import { inject, Service, Pipe } from '@angular/core';
import { CampaignFilter } from '@adgate/modules/streaming/types/campaign-filter.type';
import { environment } from '@env/environment';
import { Campaign } from '@adgate/modules/streaming/types/campaign.type';
import { CreativityFilter } from '@adgate/modules/streaming/types/creativity-filter.type';
import { catchError, concatMap, finalize, forkJoin, from, last, map, of, switchMap, tap, throwError, toArray } from 'rxjs';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { StringUtils } from '@adgate/common/components/helpers/string-utils.helper';
import { Creativity } from '@adgate/modules/streaming/types/creativity.type';
import { CreateCreativityRequest } from '../types/create-creativity-request.type';
import { Status } from '@adgate/common/types/status.type';

@Service()
export class StreamingService extends BaseService
{
    private asperaService:AsperaService = inject(AsperaService);
    private dialogService:DialogService = inject(DialogService);
    
    private baseServiceUrl = `${environment.baseServiceUrl}streaming`;

    getCampaigns(filter: CampaignFilter | null)
    {
        let url = `${this.baseServiceUrl}/getCampaigns`;
        return this.sendRequest(url, filter, 'post');
    }

    updateCampaign(c: Campaign)
    {
        let url = `${this.baseServiceUrl}/updateCampaign`;
        return this.sendRequest(url, c, 'post');
    }

    getCreativities(filter: CreativityFilter | null)
    {
        let url = `${this.baseServiceUrl}/getCreativities`;
        return this.sendRequest(url, filter, 'post');
    }

    deleteCreativity(creatitivyId: number)
    {
        let url = `${this.baseServiceUrl}/deleteCreativity/${creatitivyId}`;
        return this.sendRequest(url, null, 'delete');
    }

    // private processCreativityCreationResponse(results: RESTApplicationResponse[]): RESTApplicationResponse
    // {
    //     if( results.length === 1 ) {
    //         if( results[0].success ) {
    //             return results[0];
    //         } else {
    //             throw new Error(results[0].message);
    //         }
    //     } else {
    //         const failedCount = results.filter(r => !r.success).length;
    //         const successCount = results.filter(r => r.success).length;
    //         const specCheckFailedCount = results.filter(r => r.success).filter(r => (r.data as Creativity).status.id == Status.STREAMING_CREATIVITY_SPECERROR).length;

    //         if( failedCount != 0 || specCheckFailedCount != 0 ) {
    //             throw new Error(`Some creativities failed to upload. Please check the campaign and spec error reports for detailed information (${successCount} creativities succeeded, ${failedCount + specCheckFailedCount} creativities failed)`);
    //         } else {
    //             const result = {
    //                 success: true,
    //                 data: null,
    //                 message: 'All creativities uploaded successfully'
    //             };

    //             return result;
    //         }
    //     }
    // }

    createCreativity(requests: CreateCreativityRequest[])
    {
        let url = `${this.baseServiceUrl}/createCreativity`;
        return forkJoin(
            requests.map((req: CreateCreativityRequest) => {
                return this.sendRequest(url, req.creativity, 'post');
            })
        // ).pipe(
        //     map((results) => {
        //         return this.processCreativityCreationResponse(results);
        //     })
        );
    }

    createAndUploadCreativity_HTTP(requests: CreateCreativityRequest[])
    {
        let dh: any;
        let url = `${this.baseServiceUrl}/createCreativity`;
        let file = { data: requests[0].media, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };

        dh = this.dialogService.openUploadProgressDialog( file );

        return from(requests).pipe(
            concatMap((ccr: CreateCreativityRequest) => {
                file.data = ccr.media;
                file.state = 'in';
                file.inProgress = false;
                file.uploadedSize = 0;
                return this.getNetworkService().uploadFile(url, file, ccr.creativity).pipe(
                    catchError((err: Error) => {
                        return of({ success: false, message: err.message, data: null});
                    })
                );
            }),
            toArray(),
            // map((results) => {
            //     return this.processCreativityCreationResponse(results);
            // }),
            finalize(() => dh.close())
        );
    }

    createAndUploadCreativity_ASPERA(requests: CreateCreativityRequest[])
    {
        let createUrl = `${this.baseServiceUrl}/createCreativity`;
        let file = { data: {name: '', size: 0} as File, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };
        let dh = null;

        this.dialogService.hideProgress();
        dh = this.dialogService.openUploadProgressDialog(file);

        return from(requests).pipe(
            concatMap((ccr: CreateCreativityRequest) => {
                const filename: string = StringUtils.filterFilename(ccr.media.files[0].name);
                file.data = { name: filename, size: ccr.media.files[0].size } as File;
                file.progress = 0;
                file.uploadedSize = 0;
                
                return this.sendRequest(createUrl, ccr.creativity, 'post').pipe(
                    switchMap((r:RESTApplicationResponse) => {
                        const newCreativity: Creativity = r.data;
                        
                        return this.asperaService.upload(ccr.media).pipe(
                            tap(progress => {
                                file.progress = progress.percentage;
                                file.uploadedSize = progress.bytesTransferred || 0;
                            }),
                            last(),
                            map(finalProgress => ({
                                success: finalProgress.status === 'completed',
                                message: finalProgress.status === 'completed'
                                    ? 'Upload completed successfully'
                                    : `Upload ${finalProgress.status}`,
                                data: newCreativity
                            })),
                            switchMap((r:RESTApplicationResponse) => {
                                if( !r.success ) {
                                    return of(r);
                                } else {
                                    return this.notifyAsperaUpload(newCreativity.id, filename, newCreativity.referenceCode);
                                }
                            })
                        );
                    }),
                    catchError((err: Error) => {
                        return of({
                            success: false,
                            message: err.message,
                            data: null
                        });
                    })
                );
            }),
            toArray(),
            // map((results) => {
            //     return this.processCreativityCreationResponse(results);
            // }),
            finalize(() => dh!.close())
        );
    }

    uploadCreativity_HTTP(creativityId: number, mediaFile: File)
    {
        let dh: any;
        let url = `${this.baseServiceUrl}/uploadCreativity/${creativityId}`;
        let file = { data: mediaFile, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };

        dh = this.dialogService.openUploadProgressDialog( file );

        return this.getNetworkService().uploadFile(url, file, null).pipe(
            switchMap((r:RESTApplicationResponse) => {
                return this.testError(r);
            }),
            finalize(() => dh.close())
        );
    }

    uploadCreativity_ASPERA(creativityId: number, dataTransfer: any)
    {
        let filename = StringUtils.filterFilename(dataTransfer.files[0].name);
        let file = { data: {name: filename, size: dataTransfer.files[0].size} as File, state: 'in', inProgress: false, progress: 0, uploadedSize: 0, canRetry: false, canCancel: true };
        let dh: any;

        this.dialogService.hideProgress();
        dh = this.dialogService.openUploadProgressDialog(file);

        return this.asperaService.upload(dataTransfer).pipe(
            tap(progress => {
                file.progress = progress.percentage;
                file.uploadedSize = progress.bytesTransferred || 0;
            }),
            last(),
            map(finalProgress => ({
                success: finalProgress.status === 'completed',
                message: finalProgress.status === 'completed'
                    ? 'Upload completed successfully'
                    : `Upload ${finalProgress.status}`,
                data: null
            })),
            switchMap((r: RESTApplicationResponse) => {
                if (!r.success) {
                    return throwError(() => new Error(r.message));
                } else {
                    return this.notifyAsperaUpload(creativityId, filename, null);
                }
            }),
            finalize(() => dh.close())
        );
    }

    notifyAsperaUpload(creativityId: number, fileName: string, referenceCode: string | null)
    {
        let url = `${this.baseServiceUrl}/notifyAsperaUpload/${creativityId}`;
        let data = { fileName: fileName, referenceCode: referenceCode }

        return this.sendRequest(url, data, 'post');
    }
}
